import {
  CUBE_AGGREGATE_INTERACTION,
  CUBE_AGGREGATE_INTERACTION_EMPLOYEE,
  CUBE_AGGREGATE_INTERACTION_FIELD_FORCE,
  CUBE_AGGREGATE_INTERACTION_PRODUCT,
  CUBE_INTERACTION_AGGREGATE_PRODUCT_MESSAGE,
  CUBE_WEBSITE_AGGREGATE_INTERACTION,
} from '@/api/hcp/interaction/types';
import { AdditionalChannelTabs, Channel } from '@/models/channelFocus/index';
import parseHierarchicalFilterOldTree from '@/models/filter/utils/parseHierarchicalFilterOldTree';
import { LogicalAndFilter } from '@cubejs-client/core';
import { appendInteractionIndicationFilter } from './channelFilterHelpers';
import { buildEmailFilters } from './buildEmailChannelFilters';
import { AdditionalFilterState } from './hooks';

const buildEventFilters = (state: AdditionalFilterState): LogicalAndFilter => {
  const eventFilters: LogicalAndFilter = {
    and: [],
  };

  if (state?.eventType?.length) {
    eventFilters.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION}.activity_type`,
      operator: 'equals',
      values: [...state.eventType],
    });
  }
  if (state?.eventName?.length) {
    eventFilters.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION}.event_name`,
      operator: 'equals',
      values: [...state.eventName],
    });
  }
  if (state?.fieldForce?.length) {
    eventFilters.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION_FIELD_FORCE}.field_force_code`,
      operator: 'equals',
      values: [...state.fieldForce],
    });
  }
  if (state?.localSource?.length) {
    eventFilters.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION}.event_source_country`,
      operator: 'equals',
      values: [...state.localSource],
    });
  }
  if (state?.rocheEmployee?.length) {
    eventFilters.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION_EMPLOYEE}.employee_name`,
      operator: 'equals',
      values: [...state.rocheEmployee],
    });
  }
  return eventFilters;
};

const build1To1VisitsFilters = (state: AdditionalFilterState, indicationOptionPaths: string[]): LogicalAndFilter => {
  const channelFilters: LogicalAndFilter = {
    and: [],
  };

  if (state?.rocheEmployee?.length) {
    channelFilters.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION_EMPLOYEE}.employee_name`,
      operator: 'equals',
      values: [...state.rocheEmployee],
    });
  }

  if (state?.oneToOne?.length) {
    channelFilters.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION}.interaction_type_lvl_2`,
      operator: 'equals',
      values: [...state.oneToOne],
    });
  }
  if (state?.promo?.length) {
    channelFilters.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION}.activity_type`,
      operator: 'equals',
      values: [...state.promo],
    });
  }
  if (state?.productMessageGroup?.length) {
    channelFilters.and.push({
      member: `${CUBE_INTERACTION_AGGREGATE_PRODUCT_MESSAGE}.product_message_group`,
      operator: 'equals',
      values: [...state.productMessageGroup],
    });
  }
  if (state?.productMessageName?.length) {
    channelFilters.and.push({
      member: `${CUBE_INTERACTION_AGGREGATE_PRODUCT_MESSAGE}.product_message_name`,
      operator: 'equals',
      values: [...state.productMessageName],
    });
  }

  if (state?.type?.length) {
    const interactionValues = [];

    if (state.type.includes('Global')) {
      interactionValues.push('GMCL');
    }

    if (state.type.includes('Local')) {
      interactionValues.push('OCE-P', 'Unite');
    }

    if (interactionValues.length) {
      channelFilters.and.push({
        member: `${CUBE_AGGREGATE_INTERACTION}._tech_src_sys`,
        operator: 'equals',
        values: interactionValues,
      });
    }
  }
  if (state?.productLocal?.length) {
    channelFilters.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION_PRODUCT}.product`,
      operator: 'equals',
      values: [...state.productLocal],
    });
  }
  appendInteractionIndicationFilter(channelFilters, state, indicationOptionPaths);
  if (state?.fieldForce?.length) {
    channelFilters.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION_FIELD_FORCE}.field_force_code`,
      operator: 'equals',
      values: [...state.fieldForce],
    });
  }
  return channelFilters;
};

const buildWebsiteFilter = (state: AdditionalFilterState, indicationOptionPaths: string[]): LogicalAndFilter => {
  const websiteFilter: LogicalAndFilter = {
    and: [],
  };

  if (state?.productLocal?.length) {
    websiteFilter.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION_PRODUCT}.product`,
      operator: 'equals',
      values: [...state.productLocal],
    });
  }
  appendInteractionIndicationFilter(websiteFilter, state, indicationOptionPaths);
  if (state?.websitePortal?.length) {
    websiteFilter.and.push({
      member: `${CUBE_WEBSITE_AGGREGATE_INTERACTION}.website_name`,
      operator: 'equals',
      values: [...state.websitePortal],
    });
  }

  return websiteFilter;
};

const buildAllChannelsFilter = (state: AdditionalFilterState, indicationOptionPaths: string[]): LogicalAndFilter => {
  const allChannelsFilter: LogicalAndFilter = {
    and: [],
  };
  if (state?.interactionTypeAllChannels?.length) {
    const dimensions = [
      `${CUBE_AGGREGATE_INTERACTION}.interaction_type_lvl_1`,
      `${CUBE_AGGREGATE_INTERACTION}.interaction_type_lvl_2`,
    ];
    const parsedInteractionFilter = parseHierarchicalFilterOldTree(state?.interactionTypeAllChannels, dimensions);
    if (parsedInteractionFilter) allChannelsFilter.and.push(parsedInteractionFilter);
  }

  if (state?.fieldForce?.length) {
    allChannelsFilter.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION_FIELD_FORCE}.field_force_code`,
      operator: 'equals',
      values: [...state.fieldForce],
    });
  }

  appendInteractionIndicationFilter(allChannelsFilter, state, indicationOptionPaths);
  if (state?.productLocal?.length) {
    allChannelsFilter.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION_PRODUCT}.product`,
      operator: 'equals',
      values: [...state.productLocal],
    });
  }
  if (state?.rocheEmployee?.length) {
    allChannelsFilter.and.push({
      member: `${CUBE_AGGREGATE_INTERACTION_EMPLOYEE}.employee_name`,
      operator: 'equals',
      values: [...state.rocheEmployee],
    });
  }
  return allChannelsFilter;
};

export const buildAdditionalChannelFilters = (
  state: AdditionalFilterState,
  channel: Channel | AdditionalChannelTabs,
  indicationOptionPaths: string[],
  isEmailJourneyInsightsEnabled: boolean,
): LogicalAndFilter => {
  const globalFilters: LogicalAndFilter = {
    and: [],
  };

  if (channel === Channel.VISITS_1_TO_1) {
    const channelFilters = build1To1VisitsFilters(state, indicationOptionPaths);
    globalFilters.and.push(...channelFilters.and);
  } else if (channel === Channel.EVENT) {
    const eventFilters = buildEventFilters(state);
    globalFilters.and.push(...eventFilters.and);
  } else if (channel === Channel.EMAIL) {
    const emailFilters = buildEmailFilters(state, indicationOptionPaths, isEmailJourneyInsightsEnabled);
    globalFilters.and.push(...emailFilters.and);
  } else if (channel === Channel.WEBSITE) {
    const websiteFilters = buildWebsiteFilter(state, indicationOptionPaths);
    globalFilters.and.push(...websiteFilters.and);
  } else if (channel === AdditionalChannelTabs.ALL_CHANNEL) {
    const allChannelsFilter = buildAllChannelsFilter(state, indicationOptionPaths) || {
      and: [],
    };
    globalFilters.and.push(...allChannelsFilter.and);
  }

  return globalFilters;
};
